/**
 * Today — the capture screen, brought over from the standalone time tracker.
 *
 * The layout is that app's, near enough line for line, because a year of real
 * use shaped it and there is nothing to be gained by rediscovering the same
 * answers: one bar that both logs and times, so you pick the project once and
 * then press whichever button matches whether the work has happened yet; the
 * day's total as the largest thing on the screen; and the week underneath it,
 * because "am I behind?" is the question the day's figure always raises.
 *
 * What is NOT ported is its data model. Projects and tasks come from Todoist
 * through Project Hub's own mirror, the rate is snapshotted onto the entry at
 * creation rather than read live, and the client comes off the project. The
 * screen looks the same; what it writes is Project Hub's.
 */

import { useMemo, useRef, useState } from "react";

import { itemProjectId } from "@/components/ItemRow";
import { CalendarCard } from "@/components/CalendarCard";
import { card, eyebrow } from "@/components/atoms";
import { formatElapsed, useTimer } from "@/context/TimerContext";
import { KindPicker } from "@/components/KindPicker";
import { KIND_LABEL, defaultKind, fitKind, kindChoices, kindFields, kindOf, type EntryKind } from "@/lib/billing";
import { useToast } from "@/context/ToastContext";
import { useAllItems, useAllPhases, useProjects } from "@/hooks/useData";
import { useContinueEntry } from "@/hooks/useContinueEntry";
import { PlayIcon } from "@/components/NavIcons";
import { effectiveRate, useClients, useTimeEntries, useTimeMutations } from "@/hooks/useTime";
import { groupTasksByPhase } from "@/lib/taskOptions";
import { TaskCombo } from "@/components/TaskCombo";
import { useActivities } from "@/lib/theme";
import {
  GENERAL_WORK_LABEL, clock, clockRange, deriveEntryLabel, durationLabel, entryDayKey,
  breakdownText, hours, inBounds, parseDuration, periodBounds, toIsoLocal, totalsOf,
} from "@/lib/timeRollup";
import { useIsMobile } from "@/lib/useMediaQuery";
import type { TimeEntry } from "@/lib/types";

/** The amounts actually reached for, in the order the tracker has them. */
const PRESETS = [10, 15, 30, 45, 60, 120];
const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];

export function TodayTab({ onShowEntries, onEditEntry }: {
  onShowEntries: () => void;
  onEditEntry: (entry: TimeEntry) => void;
}) {
  const timer = useTimer();
  const continueEntry = useContinueEntry();
  const toast = useToast();
  const mobile = useIsMobile();
  const { data: entries = [] } = useTimeEntries();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: items = [] } = useAllItems();
  const { data: phases = [] } = useAllPhases();
  const { logEntry } = useTimeMutations();
  const [activities] = useActivities();

  const selectable = useMemo(
    () => projects.filter((p) => p.status !== "archived" && p.kind !== "inbox"),
    [projects],
  );

  // Capture-bar state, shared by Log and Start timer — the point of the bar is
  // that you choose once and then decide which button you wanted.
  const [projectId, setProjectId] = useState("");
  const [itemId, setItemId] = useState("");
  const [note, setNote] = useState("");
  const [minutes, setMinutes] = useState(30);
  const [customActive, setCustomActive] = useState(false);
  const [editingCustom, setEditingCustom] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const [activity, setActivity] = useState("");
  /** Billable / No charge / Internal. Null follows the project's default;
   *  once picked by hand it sticks, as long as the project still offers it. */
  const [kindPick, setKindPick] = useState<EntryKind | null>(null);
  const [busy, setBusy] = useState(false);
  const captureRef = useRef<HTMLDivElement>(null);

  const project = projects.find((p) => p.id === projectId);
  const hasClient = !!effectiveRate(project, clients).clientId;
  const kind = fitKind(kindPick ?? defaultKind(project, hasClient), project, hasClient);
  const taskGroups = useMemo(
    () => groupTasksByPhase(items, phases, projectId || undefined, itemProjectId),
    [items, phases, projectId],
  );
  const hasTasks = taskGroups.some((g) => g.tasks.length > 0);
  const task = items.find((i) => i.id === itemId);

  const today = toIsoLocal(new Date());
  const todayEntries = useMemo(
    () => entries.filter((e) => entryDayKey(e) === today)
      .sort((a, b) => (b.start || "").localeCompare(a.start || "")),
    [entries, today],
  );
  const todayTotals = totalsOf(todayEntries, projects);

  const week = useMemo(() => periodBounds("week", 0), []);
  const weekEntries = useMemo(() => entries.filter((e) => inBounds(e, week)), [entries, week]);
  const weekTotals = totalsOf(weekEntries, projects);
  const days = useMemo(() => {
    const out: { key: string; seconds: number }[] = [];
    const d = new Date(week.start);
    for (let i = 0; i < 7; i++) {
      const key = toIsoLocal(d);
      out.push({
        key,
        seconds: entries.filter((e) => entryDayKey(e) === key).reduce((n, e) => n + (e.durationSeconds || 0), 0),
      });
      d.setDate(d.getDate() + 1);
    }
    return out;
  }, [entries, week]);
  const weekMax = Math.max(1, ...days.map((d) => d.seconds));

  function chooseProject(id: string) {
    setProjectId(id);
    setItemId("");
  }

  /**
   * Picking an activity writes it into the description, unless you have typed
   * something of your own there. Clobbering a half-written note to save a word
   * of typing is a bad trade; the activity is still recorded on the entry
   * either way, which is what makes it countable later.
   */
  function chooseActivity(next: string) {
    setActivity(next);
    const typed = note.trim();
    if (!typed || activities.includes(typed)) setNote(next);
  }

  function commitCustom() {
    const mins = parseDuration(customInput);
    if (mins !== null && mins > 0) { setMinutes(mins); setCustomActive(true); }
    else if (customInput.trim()) toast("Try 1:30, 90 or 1.5h");
    setEditingCustom(false);
  }

  function resolved() {
    const r = effectiveRate(project, clients);
    return { ...r, ...kindFields(kind, r.rate) };
  }

  async function log() {
    if (minutes <= 0) return;
    setBusy(true);
    const end = new Date();
    const start = new Date(end.getTime() - minutes * 60_000);
    const r = resolved();
    try {
      await logEntry.mutateAsync({
        projectId: projectId || undefined,
        itemId: itemId || undefined,
        start: start.toISOString(), end: end.toISOString(),
        durationSeconds: minutes * 60,
        note, category: activity || undefined,
        billable: r.billable, internal: r.internal, source: "manual",
        rate: r.rate, currency: r.currency, clientId: r.clientId,
      });
      setNote("");
      toast(`Logged ${durationLabel(minutes)}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Could not save that entry");
    } finally {
      setBusy(false);
    }
  }

  function startTimer() {
    const r = resolved();
    timer.start({
      projectId: projectId || undefined,
      itemId: itemId || undefined,
      clientId: r.clientId ?? undefined,
      category: activity || undefined,
      note, kind,
    });
    setNote("");
  }

  const runningProject = projects.find((p) => p.id === timer.meta.projectId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      {/* -- Header ----------------------------------------------------------- */}
      {/* The tracker puts an <h1>Today</h1> here; that word is already the tab
          above and the page header above that, so the figure carries the row. */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
          {/* Inline, not flex: the space before the unit has to be a real
              character or the figure copies out as "5:15hours". */}
          <div className="mono" style={{ fontSize: 46, fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1 }}>
            {clock(todayTotals.seconds)}{" "}
            <span style={{ fontSize: 18, fontWeight: 500, color: "var(--muted)", letterSpacing: "normal" }}>hours</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 1, paddingBottom: 4 }}>
            <div style={{ fontSize: 14, color: "var(--ink)" }}>logged today</div>
            <div className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
              {breakdownText(todayTotals, clock) || "nothing yet"} ·{" "}
              {new Date().toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
            </div>
          </div>
        </div>

        {timer.running && (
          <button
            onClick={() => captureRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
            title="Running — scroll to the capture bar"
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "6px 12px 6px 10px",
              borderRadius: 999, border: "1px solid var(--accentLine)", background: "var(--surface)",
              cursor: "pointer",
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)" }} />
            <span className="mono" style={{ fontSize: 12, fontWeight: 500, color: "var(--accent)" }}>
              {formatElapsed(timer.seconds)}
            </span>
            {runningProject && <span style={{ fontSize: 12, color: "var(--muted)" }}>{runningProject.name}</span>}
          </button>
        )}
      </div>

      {/* -- Capture bar ------------------------------------------------------ */}
      <div ref={captureRef} style={{ ...card, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            <span style={{
              position: "absolute", left: 11, width: 8, height: 8, borderRadius: 2,
              background: project?.color ?? "var(--muted3)", pointerEvents: "none",
            }} />
            <select
              value={projectId}
              onChange={(e) => chooseProject(e.target.value)}
              aria-label="Project"
              style={{ ...field, paddingLeft: 27, paddingRight: 26, fontWeight: 500, maxWidth: 230 }}
            >
              <option value="">No project (ad-hoc)</option>
              {selectable.map((p) => (
                <option key={p.id} value={p.id}>{p.client ? `${p.name} · ${p.client}` : p.name}</option>
              ))}
            </select>
          </span>

          <TaskCombo
            value={itemId}
            onChange={setItemId}
            groups={taskGroups}
            disabled={!projectId || !hasTasks}
            emptyLabel={GENERAL_WORK_LABEL}
            style={{ ...field, maxWidth: 230, opacity: projectId && hasTasks ? 1 : 0.55 }}
          />

          {activities.length > 0 && (
            <select
              value={activity}
              onChange={(e) => chooseActivity(e.target.value)}
              aria-label="Activity"
              style={{ ...field, maxWidth: 170, color: activity ? "var(--ink)" : "var(--muted)" }}
            >
              <option value="">Activity…</option>
              {activities.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          )}

          <span style={{ marginLeft: "auto" }}>
            <KindPicker value={kind} choices={kindChoices(project, hasClient)} onChange={setKindPick} fromProject={!kindPick} />
          </span>
        </div>

        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void log(); } }}
          placeholder="What are you working on?"
          aria-label="What are you working on?"
          style={{
            width: "100%", border: "none", outline: "none", background: "transparent",
            fontSize: 19, color: "var(--ink)", padding: "2px 0",
          }}
        />

        <div style={{ height: 1, background: "var(--line)" }} />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {PRESETS.map((m) => {
              const on = !customActive && minutes === m;
              return (
                <button
                  key={m}
                  onClick={() => { setMinutes(m); setCustomActive(false); setEditingCustom(false); }}
                  className="mono"
                  style={{ ...chip, ...(on ? chipOn : null) }}
                >{durationLabel(m)}</button>
              );
            })}
            {editingCustom ? (
              <input
                autoFocus
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onBlur={commitCustom}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitCustom(); } }}
                placeholder="h:mm"
                aria-label="Custom duration"
                className="mono"
                style={{ ...chip, width: 78, textAlign: "center", border: "1px solid var(--accent)", color: "var(--ink)" }}
              />
            ) : (
              <button
                onClick={() => { setEditingCustom(true); setCustomInput(customActive ? String(minutes) : ""); }}
                style={{
                  ...chip,
                  ...(customActive ? chipOn : { border: "1px dashed var(--line2)", color: "var(--muted2)" }),
                }}
              >{customActive ? durationLabel(minutes) : "custom"}</button>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!mobile && <span style={{ fontSize: 12, color: "var(--muted2)" }}>⏎</span>}
            <button onClick={() => void log()} disabled={busy} style={primaryBtn}>
              Log {durationLabel(minutes)}
            </button>
            {timer.running ? (
              <button onClick={() => void timer.stop()} style={{ ...ghostBtn, ...chipOn }}>Stop &amp; log</button>
            ) : (
              <button onClick={startTimer} style={ghostBtn}>Start timer</button>
            )}
          </div>
        </div>
      </div>

      {/* -- From your calendar ----------------------------------------------- */}
      {/* Recent meetings as one-click entries; see Settings › Calendars. */}
      <CalendarCard entries={entries} projects={projects} />

      {/* -- Logged today ----------------------------------------------------- */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={eyebrow}>Logged today</span>
          <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
          <span className="mono" style={{ fontSize: 12.5, color: "var(--ink3)" }}>
            {todayEntries.length} {todayEntries.length === 1 ? "entry" : "entries"}
          </span>
        </div>

        {todayEntries.length === 0 ? (
          <div style={{ padding: "32px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 13.5, color: "var(--ink3)" }}>Nothing logged today.</div>
            <button onClick={startTimer} style={primaryBtn}>Start timer</button>
          </div>
        ) : (
          <div style={{ marginTop: 4 }}>
            {todayEntries.map((entry) => {
              const p = projects.find((x) => x.id === entry.project);
              return (
                <div key={entry.id} className="item-row" style={{ display: "flex", alignItems: "center", gap: 6, borderTop: "1px solid var(--tint2)" }}>
                <button
                  onClick={() => onEditEntry(entry)}
                  className="row-hover"
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 12, flex: 1, minWidth: 0,
                    padding: "10px 4px", textAlign: "left", cursor: "pointer",
                    border: "none", background: "none",
                  }}
                >
                  <span className="mono" style={{ flex: "none", width: 46, fontSize: 13.5, color: "var(--ink)", fontWeight: 500, marginTop: 1 }}>
                    {clock(entry.durationSeconds)}
                  </span>
                  <span style={{ flex: "none", width: 8, height: 8, borderRadius: 2, marginTop: 6, background: p?.color ?? "var(--muted3)" }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      display: "block", fontSize: 15, color: "var(--ink)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {deriveEntryLabel(entry.note, items.find((i) => i.id === entry.item)?.title)}
                    </span>
                    {/* Which job this was against — the description alone is
                        the same four words across four different clients. */}
                    <span style={{
                      display: "block", fontSize: 12, color: "var(--muted)", marginTop: 1,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{p?.name ?? "No project"}</span>
                  </span>
                  <span className="mono" style={{ flex: "none", fontSize: 12, color: "var(--muted)" }}>
                    {kindOf(entry, p) === "hourly" ? clockRange(entry) : KIND_LABEL[kindOf(entry, p)].toLowerCase()}
                  </span>
                </button>
                {/* Continue, as in Toggl: a timer on the same work. */}
                <button
                  className="row-time-controls"
                  onClick={() => void continueEntry(entry)}
                  title="Continue — start a timer on this again"
                  aria-label="Continue this entry"
                  style={{
                    flex: "none", width: 28, height: 28, borderRadius: 999, border: "1px solid var(--line2)",
                    background: "var(--surface)", color: "var(--ink3)", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                  }}
                ><PlayIcon size={12} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* -- Week strip ------------------------------------------------------- */}
      <div style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 20, paddingTop: 14, borderTop: "1px solid var(--line)", flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
          {days.map((d, i) => {
            const isToday = d.key === today;
            const height = d.seconds > 0 ? Math.max(3, Math.round((d.seconds / weekMax) * 56)) : 2;
            return (
              <div key={d.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div
                  title={`${d.key} — ${hours(d.seconds)} h`}
                  style={{
                    width: 22, height, borderRadius: 3,
                    background: isToday ? "var(--accent)" : d.seconds > 0 ? "var(--line2)" : "var(--line)",
                  }}
                />
                <span style={{ fontSize: 10, color: "var(--muted2)" }}>{DAY_LETTERS[i]}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>This week</span>
          <span className="mono" style={{ fontSize: 18, fontWeight: 600 }}>{hours(weekTotals.seconds)}h</span>
          <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
            {breakdownText(weekTotals, hours) || "nothing yet"}
          </span>
          <button
            onClick={onShowEntries}
            style={{ marginLeft: 8, border: "none", background: "none", color: "var(--accent)", fontSize: 13.5, fontWeight: 500, cursor: "pointer" }}
          >All entries →</button>
        </div>
      </div>
    </div>
  );
}

const field: React.CSSProperties = {
  padding: "8px 11px", borderRadius: 8, fontSize: 13.5,
  border: "1px solid var(--line2)", background: "var(--bg)", color: "var(--ink)", cursor: "pointer",
};
const chip: React.CSSProperties = {
  padding: "6px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--bg)", color: "var(--ink2)",
};
const chipOn: React.CSSProperties = {
  background: "var(--accentTint)", border: "1px solid var(--accentLine)", color: "var(--accent)",
};
const primaryBtn: React.CSSProperties = {
  padding: "8px 16px", borderRadius: 8, border: "none",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: 13.5, fontWeight: 600, cursor: "pointer",
};
const ghostBtn: React.CSSProperties = {
  padding: "8px 14px", borderRadius: 8, fontSize: 13.5, fontWeight: 500, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
